import { Box, Typography, useTheme } from '@mui/material';

import { useDateRangeLargePicker } from './hooks/useDateRangeLargePicker';
import {
  type DatePickerProps,
  DatePicker,
} from '../../mui-wrapper-components/DatePicker/DatePicker';

export type DateRangeLargePickerProps = {
  /**
   * @prop {string} id - id of the entry, also used are the key
   */
  id: string;

  /**
   * @prop {boolean} viewMode - if the component is in view mode both the start and end date pickers will be disabled
   * @default false
   */
  viewMode?: boolean;

  /**
   * @prop {number} width - the width of the date picker - set both the start and end date pickers
   * @default 212
   */
  width?: number | 'auto';

  /**
   * @prop {DatePickerProps} startDatePicker - the start date picker properties
   */
  startDatePicker: Omit<DatePickerProps, 'viewMode' | 'id'>;

  /**
   * @prop {DatePickerProp} endDatePicker - the end date picker properties
   */
  endDatePicker: Omit<DatePickerProps, 'viewMode' | 'id'>;
};

export const DateRangePicker = (props: DateRangeLargePickerProps) => {
  const { id, viewMode = false, width = 212, startDatePicker, endDatePicker } = props;

  const theme = useTheme();

  const {
    handleStartDateChange,
    handleEndDateChange,
    onStartDateError,
    onEndDateError,
    startHelpText,
    endHelperText,
  } = useDateRangeLargePicker(
    id,
    startDatePicker.minDate?.format('MM/DD/YYYY') || '',
    endDatePicker.maxDate?.format('MM/DD/YYYY') || '',
    startDatePicker,
    endDatePicker
  );

  return (
    <Box
      data-testid={id}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'normal',
        alignItems: 'center',
        width: '100%',
        height: viewMode ? theme.spacing(12) : theme.spacing(19),
        mt: theme.spacing(2.5),
      }}
    >
      <DatePicker
        {...startDatePicker}
        id={`${id}-startDate`}
        onChange={handleStartDateChange}
        inError={startHelpText !== undefined}
        width={width}
        helperText={startHelpText || ' '}
        onError={onStartDateError}
        viewMode={viewMode}
      />
      <Typography
        variant="body1"
        sx={{
          color: 'text.primary',
          mx: theme.spacing(4),
          // Negative margin offsets the dash to vertically center it with the input fields,
          // compensating for the floating label height above the outlined inputs
          marginTop: viewMode ? theme.spacing(3) : `-${theme.spacing(8)}`,
          width: theme.spacing(2.5),
        }}
      >
        —
      </Typography>
      <DatePicker
        {...endDatePicker}
        id={`${id}-endDate`}
        onChange={handleEndDateChange}
        inError={endHelperText !== undefined}
        width={width}
        helperText={endHelperText || ' '}
        onError={onEndDateError}
        viewMode={viewMode}
      />
    </Box>
  );
};
